import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import XtramModal, {
    XtramButton,
    XtramDatePicker,
    XtramField,
    XtramInput,
    XtramNotice,
    XtramSelect,
} from '../../Widgets/XtramModal/XtramModal.jsx';
import NoDocumentsPdf from '../../../assests/pdf/NoDocument.pdf';
import { formatDayMonthYear } from './otherDocumentDates';
import styles from './OtherDocumentsXtram.module.scss';

const PREVIEW_KEY = 'OtherDocumentEditPreview';
const PROCESSED_BY_SYSTEM = '-100';

const toDate = (value) => {
    if (value == null || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const sameDay = (a, b) => {
    const left = toDate(a);
    const right = toDate(b);
    if (!left || !right) return !left && !right;
    return left.getFullYear() === right.getFullYear()
        && left.getMonth() === right.getMonth()
        && left.getDate() === right.getDate();
};

// Server timestamps are UTC but may arrive without a zone suffix.
const formatDateTime = (value) => {
    const date = toDate(typeof value === 'string' && /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value) ? `${value}Z` : value);
    if (!date) return '-';
    const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    return `${formatDayMonthYear(date)} ${time}`;
};

const seedForm = (item) => ({
    title: item?.title || '',
    documentType: item?.documentType || '',
    documentDate: toDate(item?.documentDate),
    uploadDate: toDate(item?.uploadDate),
    uploadNumber: item?.uploadNumber != null ? String(item.uploadNumber) : '',
    processedBy: item?.processedBy || '',
});

const parseUploadNumber = (value) => (/^\d+$/.test(String(value).trim()) ? Number(value) : NaN);

// Any open popup (select list, date picker, transfer dialog) owns the keyboard.
const hasOpenPopup = () => Array.from(
    document.querySelectorAll('[role="dialog"], [role="presentation"], [role="listbox"]')
).some((el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden');

const KeyChip = ({ children }) => <span className={styles.keyChip}>{children}</span>;

const ActionTile = ({ icon, label, onClick, disabled = false, danger = false }) => (
    <button
        type="button"
        className={`${styles.actionTile} ${danger ? styles.actionTileDanger : ''}`}
        onClick={onClick}
        disabled={disabled}
    >
        {icon}
        <span>{label}</span>
    </button>
);

/**
 * View and edit every field of one other document. `onSave(payload)` resolves to true on success;
 * `uploadDate`, `processedBy` and `processed` are only sent when they changed.
 * `onAttachFiles(item, files)` and `onDeleteFile(item)` resolve to booleans.
 * Keys: ESC closes, F1 marks processed, F2 saves, F3 next, F4 previous.
 */
const OtherDocumentEditModal = ({
    open,
    onClose,
    onSave,
    onDelete,
    onAttachFiles,
    onDeleteFile,
    onPrev,
    onNext,
    t,
    direction,
    item,
    index = 0,
    total = 0,
    typeOptions = [],
    subAccountantOptions = [],
    sectionTitle,
    clientName,
    isUnprocessed = false,
    canChangeStatus = false,
    canEditAdminFields = false,
    canDelete = false,
    transferAction = null,
    suspendKeys = false,
    busy = false,
}) => {
    const [form, setForm] = useState(() => seedForm(item));
    const [processed, setProcessed] = useState(!isUnprocessed);
    const [submitted, setSubmitted] = useState(false);
    const [showPreview, setShowPreview] = useState(() => localStorage.getItem(PREVIEW_KEY) !== 'false');
    const [attachOpen, setAttachOpen] = useState(false);
    const [attachFiles, setAttachFiles] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [confirmFileDelete, setConfirmFileDelete] = useState(false);
    const [fileBusy, setFileBusy] = useState(false);

    useEffect(() => {
        if (!open || !item) return;
        setForm(seedForm(item));
        setProcessed(!isUnprocessed);
        setSubmitted(false);
        // Re-seed only when another document is shown, not when the list reloads.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, item?.id]);

    useEffect(() => {
        if (open) setProcessed(!isUnprocessed);
    }, [open, isUnprocessed]);

    useEffect(() => {
        if (open) return;
        setAttachOpen(false);
        setAttachFiles([]);
        setConfirmFileDelete(false);
    }, [open]);

    const categoryOptions = useMemo(() => {
        const options = typeOptions.filter((type) => type?.name).map((type) => ({ value: type.name, label: type.name }));
        if (item?.documentType && !options.some((option) => option.value === item.documentType)) {
            options.push({ value: item.documentType, label: item.documentType });
        }
        return options;
    }, [typeOptions, item?.documentType]);

    const processedByOptions = useMemo(() => {
        const options = [
            { value: '', label: t('None') },
            { value: PROCESSED_BY_SYSTEM, label: t('ProcessedBySystem') },
            ...subAccountantOptions.filter((option) => option?.value),
        ];
        if (item?.processedBy && !options.some((option) => option.value === item.processedBy)) {
            options.push({ value: item.processedBy, label: item.processedBy });
        }
        return options;
    }, [subAccountantOptions, item?.processedBy, t]);

    const uploadNumberValue = parseUploadNumber(form.uploadNumber);
    const missing = {
        title: !form.title.trim(),
        documentType: !form.documentType,
        documentDate: !form.documentDate,
        uploadDate: !form.uploadDate,
        uploadNumber: Number.isNaN(uploadNumberValue),
    };
    const statusChanged = canChangeStatus && processed === isUnprocessed;
    const uploadDateChanged = !sameDay(form.uploadDate, item?.uploadDate);
    const processedByChanged = form.processedBy !== (item?.processedBy || '');
    const dirty = !!item && (
        form.title.trim() !== (item.title || '')
        || form.documentType !== (item.documentType || '')
        || !sameDay(form.documentDate, item.documentDate)
        || uploadDateChanged
        || uploadNumberValue !== item.uploadNumber
        || processedByChanged
        || statusChanged
    );
    const working = busy || fileBusy;
    const hasPrev = index > 0;
    const hasNext = index > -1 && index < total - 1;

    const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));
    const fieldError = (field) => (submitted && missing[field] ? t('RequiredField') : null);

    const handleSave = async () => {
        setSubmitted(true);
        if (!item || working || Object.values(missing).some(Boolean)) return;
        await onSave({
            title: form.title.trim(),
            documentType: form.documentType,
            documentDate: form.documentDate,
            uploadDate: uploadDateChanged ? form.uploadDate : undefined,
            uploadNumber: canEditAdminFields ? uploadNumberValue : undefined,
            processedBy: canEditAdminFields && processedByChanged ? form.processedBy : undefined,
            processed: statusChanged ? processed : undefined,
        });
    };

    const runFileAction = async (action) => {
        setFileBusy(true);
        try {
            return await action();
        } finally {
            setFileBusy(false);
        }
    };

    const closeAttach = () => {
        if (fileBusy) return;
        setAttachOpen(false);
        setAttachFiles([]);
    };

    const handleUploadAttach = async () => {
        if (!attachFiles.length || !item) return;
        if (await runFileAction(() => onAttachFiles(item, attachFiles))) {
            setAttachOpen(false);
            setAttachFiles([]);
        }
    };

    const handleDeleteFile = async () => {
        if (!item) return;
        if (await runFileAction(() => onDeleteFile(item))) setConfirmFileDelete(false);
    };

    const togglePreview = () => {
        setShowPreview((prev) => {
            localStorage.setItem(PREVIEW_KEY, String(!prev));
            return !prev;
        });
    };

    const keyStateRef = useRef(null);
    keyStateRef.current = {
        nested: suspendKeys || attachOpen || confirmFileDelete,
        working,
        canChangeStatus,
        hasNext,
        hasPrev,
        onClose,
        onNext,
        onPrev,
        handleSave,
        closeNested: () => {
            if (attachOpen) closeAttach();
            else if (confirmFileDelete && !fileBusy) setConfirmFileDelete(false);
        },
    };

    useEffect(() => {
        if (!open) return undefined;
        const functionKey = (event) => {
            if (typeof event?.key === 'string' && /^F\d{1,2}$/.test(event.key)) return event.key;
            if (typeof event?.code === 'string' && /^F\d{1,2}$/.test(event.code)) return event.code;
            return null;
        };
        const handleKeyDown = (event) => {
            const state = keyStateRef.current;
            if (event.key === 'Escape') {
                if (state.nested) {
                    state.closeNested();
                    return;
                }
                if (!hasOpenPopup() && !state.working) state.onClose();
                return;
            }
            const key = functionKey(event);
            if (!['F1', 'F2', 'F3', 'F4'].includes(key) || state.nested || hasOpenPopup()) return;
            event.preventDefault();
            event.stopPropagation();
            if (state.working) return;
            if (key === 'F1' && state.canChangeStatus) setProcessed(true);
            if (key === 'F2') state.handleSave();
            if (key === 'F3' && state.hasNext) state.onNext();
            if (key === 'F4' && state.hasPrev) state.onPrev();
        };
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [open]);

    if (!item) return null;

    const processedByLabel = item.processedBy
        ? processedByOptions.find((option) => option.value === item.processedBy)?.label || item.processedBy
        : '';
    const info = [
        { label: t('OtherDocumentsTitle'), value: sectionTitle || t('DefaultSection') },
        { label: t('Client'), value: clientName ? `${clientName} (${item.caseId})` : item.caseId },
        { label: t('CreatedDate'), value: formatDateTime(item.createdAt) },
        { label: t('LastUpdated'), value: formatDateTime(item.modified) },
    ];
    const PrevIcon = direction === 'rtl' ? ChevronRightIcon : ChevronLeftIcon;
    const NextIcon = direction === 'rtl' ? ChevronLeftIcon : ChevronRightIcon;
    const counter = total > 0 && index > -1 ? ` (${index + 1} / ${total})` : '';

    return (
        <>
            <XtramModal
                open={open}
                onClose={onClose}
                title={`${t('Document')} ${item.uploadNumber ?? ''}${counter}`}
                direction={direction}
                expanded={showPreview}
                busy={working}
            >
                <div className={`${styles.editLayout} ${showPreview ? '' : styles.noPreview}`}>
                    <div className={styles.compactForm}>
                        <div className={styles.fieldGrid}>
                            <XtramField label={t('Title')} required error={fieldError('title')}>
                                <XtramInput
                                    direction={direction}
                                    value={form.title}
                                    onChange={(e) => setField('title')(e.target.value)}
                                    placeholder={t('Title')}
                                    error={submitted && missing.title}
                                    disabled={working}
                                />
                            </XtramField>
                            <XtramField label={t('Document Type')} required error={fieldError('documentType')}>
                                <XtramSelect
                                    direction={direction}
                                    options={categoryOptions}
                                    value={categoryOptions.find((option) => option.value === form.documentType) || null}
                                    onChange={(option) => setField('documentType')(option?.value || '')}
                                    placeholder={t('Document Type')}
                                    error={submitted && missing.documentType}
                                    disabled={working}
                                    disableClearable
                                />
                            </XtramField>
                            <XtramField label={t('Document Date')} required error={fieldError('documentDate')}>
                                <XtramDatePicker
                                    value={form.documentDate}
                                    onChange={setField('documentDate')}
                                    error={submitted && missing.documentDate}
                                    disabled={working}
                                />
                            </XtramField>
                            <XtramField label={t('Upload Date')} required error={fieldError('uploadDate')}>
                                <XtramDatePicker
                                    value={form.uploadDate}
                                    onChange={setField('uploadDate')}
                                    error={submitted && missing.uploadDate}
                                    disabled={working}
                                />
                            </XtramField>
                            <XtramField label={t('UploadNumber')} required error={fieldError('uploadNumber')}>
                                <XtramInput
                                    direction={direction}
                                    value={form.uploadNumber}
                                    onChange={(e) => setField('uploadNumber')(e.target.value.replace(/[^\d]/g, ''))}
                                    inputProps={{ inputMode: 'numeric' }}
                                    error={submitted && missing.uploadNumber}
                                    disabled={working || !canEditAdminFields}
                                />
                            </XtramField>
                            <XtramField label={t('DocumentProcessedBy')}>
                                {canEditAdminFields ? (
                                    <XtramSelect
                                        direction={direction}
                                        options={processedByOptions}
                                        value={processedByOptions.find((option) => option.value === form.processedBy) || processedByOptions[0]}
                                        onChange={(option) => setField('processedBy')(option?.value || '')}
                                        disabled={working}
                                        disableClearable
                                    />
                                ) : (
                                    <XtramInput direction={direction} value={processedByLabel || '-'} disabled />
                                )}
                            </XtramField>
                            {canChangeStatus && (
                                <XtramField label={t('status')} required className={styles.spanAll}>
                                    <div className={styles.statusToggle}>
                                        <button
                                            type="button"
                                            className={!processed ? styles.statusActive : ''}
                                            onClick={() => setProcessed(false)}
                                            disabled={working}
                                        >
                                            <ScheduleIcon sx={{ fontSize: 16 }} />
                                            {t('unprocessed')}
                                        </button>
                                        <button
                                            type="button"
                                            className={processed ? styles.statusActive : ''}
                                            onClick={() => setProcessed(true)}
                                            disabled={working}
                                        >
                                            <CheckCircleIcon sx={{ fontSize: 16 }} />
                                            {t('processed')}
                                            <KeyChip>F1</KeyChip>
                                        </button>
                                    </div>
                                </XtramField>
                            )}
                        </div>

                        <div className={styles.actionsPanel}>
                            <div className={styles.navRow}>
                                <XtramButton variant="secondary" onClick={onPrev} disabled={working || !hasPrev}>
                                    <PrevIcon fontSize="small" />
                                    {t('Previous')}
                                    <KeyChip>F4</KeyChip>
                                </XtramButton>
                                <XtramButton onClick={handleSave} disabled={working} busy={busy}>
                                    {t('Save')}
                                    <KeyChip>F2</KeyChip>
                                </XtramButton>
                                <XtramButton variant="secondary" onClick={onNext} disabled={working || !hasNext}>
                                    <KeyChip>F3</KeyChip>
                                    {t('Next')}
                                    <NextIcon fontSize="small" />
                                </XtramButton>
                            </div>

                            {processedByLabel && (
                                <div className={styles.processedBadge}>
                                    {t('DocumentProcessedBy')}: {processedByLabel}
                                </div>
                            )}

                            <div className={styles.actionTiles}>
                                <ActionTile
                                    icon={showPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    label={showPreview ? t('HideDocument') : t('ShowDocument')}
                                    onClick={togglePreview}
                                />
                                {canDelete && (
                                    <>
                                        <ActionTile
                                            icon={<AttachFileIcon sx={{ color: '#2196F3' }} />}
                                            label={t('AttachDocument')}
                                            onClick={() => setAttachOpen(true)}
                                            disabled={working}
                                        />
                                        {item.documentUrl && (
                                            <ActionTile
                                                icon={<LinkOffIcon sx={{ color: '#F44336' }} />}
                                                label={t('DeleteDocument')}
                                                onClick={() => setConfirmFileDelete(true)}
                                                disabled={working}
                                            />
                                        )}
                                        {transferAction && (
                                            <div
                                                className={styles.actionTile}
                                                onClick={(e) => {
                                                    if (!e.target.closest('button')) e.currentTarget.querySelector('button')?.click();
                                                }}
                                            >
                                                {transferAction}
                                                <span>{t('TransferDocument')}</span>
                                            </div>
                                        )}
                                        <ActionTile
                                            icon={<DeleteIcon sx={{ color: '#fa0a1e' }} />}
                                            label={t('Delete')}
                                            onClick={() => onDelete(item)}
                                            disabled={working}
                                            danger
                                        />
                                    </>
                                )}
                            </div>
                        </div>

                        <div className={styles.infoPills}>
                            {info.map((entry) => (
                                <span key={entry.label} className={styles.infoPill} title={entry.value}>
                                    <span>{entry.label}:</span>
                                    <strong>{entry.value}</strong>
                                </span>
                            ))}
                            <Tooltip title={item.id}>
                                <span className={styles.infoPill}>
                                    <span>{t('OtherDocumentsDocumentId')}:</span>
                                    <strong className={styles.idText}>{String(item.id).slice(0, 8)}</strong>
                                </span>
                            </Tooltip>
                            {dirty && (
                                <span className={`${styles.infoPill} ${styles.infoPillWarning}`}>{t('OtherDocumentsUnsavedChanges')}</span>
                            )}
                        </div>
                    </div>


                    {showPreview && (
                        <div className={styles.preview}>
                            <iframe
                                key={item.documentUrl || item.id}
                                src={`${item.documentUrl || NoDocumentsPdf}#page=1&view=FitH`}
                                title={item.title || t('Document')}
                                allowFullScreen
                            />
                        </div>
                    )}
                </div>
            </XtramModal>

            <XtramModal
                open={open && attachOpen}
                onClose={closeAttach}
                title={t('AttachDocument')}
                direction={direction}
                busy={fileBusy}
                footer={(
                    <>
                        <XtramButton variant="secondary" onClick={closeAttach} disabled={fileBusy}>{t('cancel')}</XtramButton>
                        <XtramButton onClick={handleUploadAttach} disabled={fileBusy || attachFiles.length === 0} busy={fileBusy}>
                            {t('Upload')}
                        </XtramButton>
                    </>
                )}
            >
                <div className={styles.stack}>
                    {item.documentUrl && <XtramNotice type="info">{t('OtherDocumentsAttachMergeHint')}</XtramNotice>}
                    <XtramField label={t('uploadDocument')} required size="full">
                        <label
                            className={`${styles.dropZone} ${dragging ? styles.dragging : ''}`}
                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragging(false);
                                setAttachFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files || [])]);
                            }}
                        >
                            <input
                                type="file"
                                multiple
                                onChange={(e) => {
                                    const picked = Array.from(e.target.files || []);
                                    setAttachFiles((prev) => [...prev, ...picked]);
                                    e.target.value = '';
                                }}
                            />
                            <CloudUploadOutlinedIcon className={styles.dropIcon} />
                            <span>{t('DragDropFilesOrClick')}</span>
                        </label>
                    </XtramField>
                    {attachFiles.length > 0 && (
                        <ul className={styles.fileList}>
                            {attachFiles.map((file, fileIndex) => (
                                <li key={`${file.name}-${fileIndex}`} className={styles.fileChip} title={file.name}>
                                    <span>{file.name}</span>
                                    <IconButton
                                        size="small"
                                        aria-label={t('Delete')}
                                        onClick={() => setAttachFiles((prev) => prev.filter((_, i) => i !== fileIndex))}
                                        disabled={fileBusy}
                                    >
                                        <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </XtramModal>

            <XtramModal
                open={open && confirmFileDelete}
                onClose={() => setConfirmFileDelete(false)}
                title={t('DeleteDocument')}
                direction={direction}
                busy={fileBusy}
                footer={(
                    <>
                        <XtramButton variant="secondary" onClick={() => setConfirmFileDelete(false)} disabled={fileBusy}>{t('Cancel')}</XtramButton>
                        <XtramButton variant="danger" onClick={handleDeleteFile} disabled={fileBusy} busy={fileBusy}>{t('Delete')}</XtramButton>
                    </>
                )}
            >
                <XtramNotice type="warning">{t('DeleteDocumentUrlConfirmation')}</XtramNotice>
            </XtramModal>
        </>
    );
};

export default OtherDocumentEditModal;
